export type ShapeSetSelection = 'selected' | 'page' | 'all';
export type ShapeSetOperation = 'create' | 'update' | 'delete';

export interface IShapeSetMeta {
    name: string;
    description?: string;
    tags?: string[];
}

export interface IShapeSetCurrentRef {
    id: string;
    version: number;
    meta?: Partial<IShapeSetMeta>;
    shapes?: IShapeSetUpdateShapeRef[];
    shapeIds?: string[];
}

export interface IShapeSetUpdateShapeRef {
    defId: string;
    version: number;
    meta: {
        id: string;
    };
}

export interface IShapeSetItemMeta {
    id: string;
    [key: string]: any;
}

export interface IShapeSetExportRequestData {
    requestId: string;
    selection?: ShapeSetSelection;
    operation?: ShapeSetOperation;
    currentSet?: IShapeSetCurrentRef;
    meta?: Partial<IShapeSetMeta>;
}

export interface IShapeSetItem {
    defId: string;
    version: number;
    meta: IShapeSetItemMeta;
    style?: Record<string, any>;
    typeStyle?: {
        bounds?: {
            width: number;
            height: number;
        };
    };
    scaleX?: number;
    scaleY?: number;
    angle?: number;
    defaultBounds?: {
        width: number;
        height: number;
    };
    shapeContext?: any;
    textStyle?: Record<string, { content: Array<Record<string, any>> }>;
    drawCode?: {
        instructions: string[];
        defaultWidth?: number;
        defaultHeight?: number;
        scaleX?: number;
        scaleY?: number;
        lineThickness?: number;
    };
    data?: Record<string, any>;
}

export interface IShapeSetPayload {
    schemaVersion: 1;
    id: string;
    version: number;
    meta: IShapeSetMeta;
    items: IShapeSetItem[];
}

export interface IShapeSetExportResultData {
    requestId: string;
    set: IShapeSetPayload;
}

export interface IShapeSetImportRequestData {
    requestId?: string;
    set: IShapeSetPayload;
    autoOpen?: boolean;
}

export type ShapeSetImportAction = 'created' | 'updated' | 'ignored';

export interface IShapeSetImportResultData {
    requestId?: string;
    setId: string;
    version: number;
    action: ShapeSetImportAction;
}

export type ShapeSetErrorCode =
    'INVALID_REQUEST' |
    'EMPTY_SELECTION' |
    'UNSUPPORTED_SHAPE_TYPE' |
    'UNSUPPORTED_DEF' |
    'DUPLICATE_ITEM' |
    'VERSION_CONFLICT' |
    'PAYLOAD_TOO_LARGE' |
    'INTERNAL_ERROR';

export interface IShapeSetErrorData {
    requestId?: string;
    event: 'export' | 'import';
    code: ShapeSetErrorCode;
    message: string;
    details?: Record<string, any>;
}

export interface IShapeSetUpsertResult {
    action: ShapeSetImportAction;
    set: IShapeSetPayload;
}

/**
 * Domain error wrapper for shape-set export/import validation and conflict handling.
 */
export class ShapeSetError extends Error {
    /**
     * Creates a typed shape-set error with optional structured details payload.
     */
    constructor(
        public readonly code: ShapeSetErrorCode,
        public readonly details?: Record<string, any>,
        message?: string,
    ) {
        super( message || code );
        this.name = 'ShapeSetError';
    }
}

export const SHAPE_SET_SCHEMA_VERSION = 1;

export const SHAPE_SET_EVENTS = {
    exportRequest: 'creately:shapeset:export:request',
    exportResult: 'creately:shapeset:export:result',
    import: 'creately:shapeset:import',
    importResult: 'creately:shapeset:import:result',
    error: 'creately:shapeset:error',
} as const;
